import React from 'react';
import { useLocation } from 'wouter';
import { useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import getTransferTableOptions from './transferOptions';
import type { Transfer } from '../../common/types/transfer';
import type { DecryptedItem } from '../../common/types/items';
import ConfirmationDialog from '../../common/components/ConfirmationDialog';
import { AppDispatch } from '../../store';
import { deleteTransfer, loadTransfers } from '../transfersReducer';

type Props = {
  searchValue: string;
  showFinishedTransfers: boolean;
  transfers: DecryptedItem<Transfer>[];
};

function TransfersTable({ searchValue, showFinishedTransfers, transfers }: Props) {
  const dispatch: AppDispatch = useDispatch();
  const [location, setLocation] = useLocation();
  const { table, sort, filter } = getTransferTableOptions();
  const [sorting, setSorting] = React.useState<{ sortable: string; order: 'ASC' | 'DESC' }>({
    sortable: 'appointmentDate',
    order: 'DESC',
  });

  const filteredTransfers = transfers
    .filter(({ payload }) => filter(searchValue, payload) && (showFinishedTransfers ? true : payload.state === false))
    .sort((transferA, transferB) => sort(transferA.payload, transferB.payload, sorting));

  return (
    <Paper variant='outlined' sx={{ height: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: '100%' }}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead
            sx={{
              '& th': { fontWeight: 'bold', background: '#F2F2F2' },
            }}
          >
            <TableRow>
              {table.map((option) => {
                const { headline, sortable } = option;
                return (
                  <TableCell key={`header_${headline}`}>
                    {headline}
                    {sortable != null && (
                      <IconButton
                        color={sorting.sortable === sortable ? 'primary' : 'secondary'}
                        aria-label='sort'
                        sx={{ marginLeft: '8px' }}
                        onClick={() => {
                          const isSame = sorting.sortable === sortable;
                          const order =
                            isSame && sorting.order === 'ASC'
                              ? 'DESC'
                              : isSame && sorting.order === 'DESC'
                              ? 'ASC'
                              : 'ASC';
                          setSorting({ sortable: sortable, order });
                        }}
                      >
                        {sorting.sortable != sortable && <UnfoldMoreIcon fontSize='small' />}
                        {sorting.sortable === sortable && sorting.order === 'ASC' && (
                          <KeyboardArrowUpIcon fontSize='small' />
                        )}
                        {sorting.sortable === sortable && sorting.order === 'DESC' && (
                          <KeyboardArrowDownIcon fontSize='small' />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredTransfers.map(({ id, payload }) => (
              <TableRow
                key={id}
                sx={{
                  height: '54px',
                  '&:hover': {
                    cursor: 'pointer',
                    background: '#FAFAFA',
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
                onClick={() => setLocation(`${location}/${id}`)}
              >
                {table.map((option) => {
                  if (option.type === 'text') {
                    return <TableCell key={`body_${option.headline}`}>{option.value(payload)}</TableCell>;
                  }
                  if (option.type === 'boolean') {
                    return (
                      <TableCell key={`body_${option.headline}`}>
                        {option.value(payload) ? (
                          <CheckCircleOutlineIcon fontSize='small' />
                        ) : (
                          <RemoveCircleOutlineIcon fontSize='small' />
                        )}
                      </TableCell>
                    );
                  }
                  if (option.type === 'delete') {
                    return (
                      <TableCell key={`body_${option.headline}_${option.type}`}>
                        <ConfirmationDialog
                          button={(onClick) => (
                            <IconButton
                              aria-label='add client'
                              onClick={(event) => {
                                event.stopPropagation();
                                onClick();
                              }}
                            >
                              <DeleteIcon fontSize='small' color='primary' />
                            </IconButton>
                          )}
                          dialogTitle='Klient*in löschen'
                          dialogText='Möchtest du die/den Klient*in wirklich löschen?'
                          dialogConfirmText='Löschen'
                          onConfirm={async () => {
                            await dispatch(deleteTransfer(id));
                            await dispatch(loadTransfers());
                          }}
                        />
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TransfersTable;
